/**
 * ContactPage — public marketing contact form (s92). Posts to /api/contact, which
 * forwards to CONTACT_EMAIL via Resend. Uses the shared PublicLayout (landing nav +
 * footer). Brand inline-style idiom matches LandingPage.
 */
import { useState } from "react";
import { PublicLayout, CONTAINER } from "@/components/public/PublicLayout";
import { Loader2 } from "lucide-react";
import { DOMAIN, FONT, PRODUCT_NAME, SAGE, WARM } from "@shared/brand";
import { Seo } from "@/components/public/Seo";

const label: React.CSSProperties = { display: "block", fontSize: 14, fontWeight: 500, color: WARM.charcoal, marginBottom: 7 };
const input: React.CSSProperties = {
  width: "100%", fontSize: 15, color: WARM.charcoal, background: WARM.white,
  border: `1px solid ${WARM.stone}`, borderRadius: 8, padding: "11px 13px", fontFamily: "inherit", outline: "none",
};

type Status = "idle" | "submitting" | "success" | "error";

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [status, setStatus] = useState<Status>("idle");
  const [errorMsg, setErrorMsg] = useState<string>("");

  const set = (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus("submitting");
    setErrorMsg("");
    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (res.status === 429) {
        setStatus("error");
        setErrorMsg("You've sent a few messages already. Please wait an hour and try again.");
        return;
      }
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setStatus("error");
        setErrorMsg((body as { error?: string }).error ?? "Something went wrong. Please try again.");
        return;
      }
      setStatus("success");
    } catch {
      setStatus("error");
      setErrorMsg("Couldn't reach the server. Please check your connection and try again.");
    }
  };

  return (
    <PublicLayout>
      <Seo path="/contact" />
      <section style={{ background: `radial-gradient(110% 80% at 50% 0%, ${SAGE[50]} 0%, ${WARM.cream} 60%)`, padding: "72px 32px 88px", minHeight: "70vh" }}>
        <div className={CONTAINER} style={{ maxWidth: 640 }}>
          <div style={{ textAlign: "center", marginBottom: 40 }}>
            <h1 style={{ fontFamily: FONT.serif, fontWeight: 400, fontSize: 44, lineHeight: 1.1, letterSpacing: "-0.015em", color: WARM.ink, margin: "0 0 16px" }}>
              Get in touch
            </h1>
            <p style={{ fontSize: 17, lineHeight: 1.6, color: WARM.taupe, margin: "0 auto", maxWidth: 520 }}>
              Questions about {PRODUCT_NAME}? Want to become an advisor partner? We'd love to hear from you.
            </p>
          </div>

          <div style={{ background: WARM.white, border: "1px solid #E7E1D5", borderRadius: 16, padding: "32px 30px" }}>
            {status === "success" ? (
              <div style={{ textAlign: "center", padding: "24px 8px" }}>
                <h2 style={{ fontFamily: FONT.serif, fontWeight: 500, fontSize: 22, color: WARM.ink, margin: "0 0 10px" }}>Thanks — we'll get back to you shortly.</h2>
                <p style={{ fontSize: 15, lineHeight: 1.6, color: "#5A554A", margin: "0 0 22px" }}>
                  Your message is on its way. We typically reply within one business day.
                </p>
                <button
                  onClick={() => { setForm({ name: "", email: "", subject: "", message: "" }); setStatus("idle"); }}
                  style={{ fontSize: 15, color: WARM.charcoal, border: `1px solid ${WARM.stone}`, padding: "11px 22px", borderRadius: 8, background: "none", cursor: "pointer", fontFamily: "inherit" }}
                >
                  Send another message
                </button>
              </div>
            ) : (
              <form onSubmit={onSubmit} style={{ display: "flex", flexDirection: "column", gap: 18 }}>
                <div>
                  <label htmlFor="name" style={label}>Name</label>
                  <input id="name" type="text" required value={form.name} onChange={set("name")} style={input} placeholder="Your name" />
                </div>
                <div>
                  <label htmlFor="email" style={label}>Email</label>
                  <input id="email" type="email" required value={form.email} onChange={set("email")} style={input} placeholder="you@example.com" />
                </div>
                <div>
                  <label htmlFor="subject" style={label}>Subject <span style={{ color: WARM.taupe, fontWeight: 400 }}>(optional)</span></label>
                  <input id="subject" type="text" value={form.subject} onChange={set("subject")} style={input} placeholder="What's this about?" />
                </div>
                <div>
                  <label htmlFor="message" style={label}>Message</label>
                  <textarea id="message" required rows={6} value={form.message} onChange={set("message")} style={{ ...input, resize: "vertical" }} placeholder="How can we help?" />
                </div>

                {status === "error" && (
                  <p style={{ fontSize: 14, color: "#A84234", margin: 0 }}>{errorMsg}</p>
                )}

                <div>
                  <button
                    type="submit"
                    disabled={status === "submitting"}
                    style={{ display: "inline-flex", alignItems: "center", gap: 9, fontSize: 16, color: WARM.white, background: SAGE[600], padding: "14px 28px", borderRadius: 8, fontWeight: 500, border: "none", cursor: status === "submitting" ? "default" : "pointer", opacity: status === "submitting" ? 0.7 : 1, fontFamily: "inherit" }}
                  >
                    {status === "submitting" && <Loader2 size={17} className="animate-spin" />}
                    {status === "submitting" ? "Sending…" : "Send message"}
                  </button>
                </div>
              </form>
            )}
          </div>

          {/*
            IDENTIFICATION BLOCK (s216, SEO phase 1 item 2).

            A crawlable email address and a postal address are a trust signal, and CASL s.6(2)
            requires the sender's mailing address on commercial electronic messages — so the site
            should state the same thing the emails do.

            🔴 THE ADDRESS IS A PLACEHOLDER, AND IT IS THE ONE READE ALREADY USES. Taken from the
            company system's identification block, which reads:
              "Organized Estate Inc., [mailing address], reade@organized-estate.com"
            (`estate-planning/marketing/funnel-project.md` §3). The literal "[mailing address]" is
            his placeholder, not a value invented here. Reade replaces it with the real registered
            address before launch — see the s216 results note's open items.

            The email is a real `mailto:` anchor, so it is both crawlable and clickable — the
            contact form alone gave a crawler nothing and gave a visitor no alternative if the
            form failed.
          */}
          <div style={{ marginTop: 28, textAlign: "center", fontSize: 14, lineHeight: 1.7, color: WARM.taupe }}>
            <div>
              Prefer email?{" "}
              <a href={`mailto:reade@${DOMAIN.primary}`} style={{ color: SAGE[600], textDecoration: "none" }}>
                {`reade@${DOMAIN.primary}`}
              </a>
            </div>
            <div style={{ marginTop: 4 }}>
              {PRODUCT_NAME} Inc. · [mailing address] · Made in Canada
            </div>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
